import type {
  FieldSpec,
  QueryRequest,
} from '@moment/horizon-data-access/platform'
import type { AbbreviatedAccountsListQueryVariables } from '@/graphql/generated/schema'
import type {
  AccountsListQueryVariables,
  PositionsForInstrumentQuery,
  PositionsForInstrumentQueryVariables,
} from '@/graphql/generated/schema.pre-account-migration'
import type {
  GetAbbreviatedAccountListResponse,
  GetAccountListResponse,
} from '@/hooks/use-get-abbreviated-account-data'
import type {
  AbbreviatedAccount,
  AccountIdsResponse,
  AccountListAccount,
  AccountQuerySleeve,
  AccountSortInput,
  AccountStatus,
  AccountStatusesParams,
  SormPage,
  SormRow,
} from './account-query-sorm-types'

import {
  createQueryRequest,
  executeSormQuery,
} from '@moment/horizon-data-access'

import {
  ABBREVIATED_ACCOUNT_MARGIN_SORM_FIELDS,
  ABBREVIATED_ACCOUNT_SORM_FIELDS,
  ABBREVIATED_ACCOUNT_STATISTICS_SORM_FIELDS,
  ACCOUNT_IDS_SORM_FIELDS,
  ACCOUNT_LIST_SORM_FIELDS,
  ACCOUNT_LIST_WITH_MARGIN_SORM_FIELDS,
  ACCOUNT_QUERY_SORM_QUERY_NAMES,
  ACCOUNT_STATUS_SORM_FIELDS,
  ACCOUNT_STATUS_WITH_MARGIN_SORM_FIELDS,
  DEFAULT_ACCOUNT_LIST_LIMIT,
  POSITIONS_FOR_INSTRUMENT_SORM_FIELDS,
  SLEEVE_SORM_FIELDS,
  SORM_PAGE_SIZE,
} from './constants'
import { groupPositionsByAdvisor } from './helpers/group-positions-by-advisor'
import { groupSleevesByAccount } from './helpers/group-sleeves-by-account'
import {
  mapAccountExtra,
  mapAccountStatistics,
  mapCashLots,
  mapCustodian,
  mapMomentStatus,
} from './helpers/map-account-row-fields'
import {
  readSormNullableBoolean,
  readSormNullableString,
  readSormNumber,
  readSormString,
} from './helpers/sorm-row-readers'
import { translateAccountFilters } from './helpers/translate-account-filters'
import { translateAccountSort } from './helpers/translate-account-sort'

type AccountListParams = AccountsListQueryVariables & {
  include_margin_fields?: boolean
}

interface AccountPageParams {
  limit?: number | null
  offset?: number | null
  filters?:
    | AccountsListQueryVariables['filters']
    | AbbreviatedAccountsListQueryVariables['filters']
  sort?: AccountSortInput | AccountSortInput[] | null
}

async function querySormPage(
  request: QueryRequest,
  queryName: string,
  signal: AbortSignal | undefined,
): Promise<SormPage> {
  const response = await executeSormQuery(request, { queryName, signal })
  const rows = response.data.data ?? []
  return { rows, totalCount: response.data.metadata.total_count }
}

async function querySormAll(
  request: QueryRequest,
  queryName: string,
  signal: AbortSignal | undefined,
): Promise<SormRow[]> {
  const rows: SormRow[] = []
  let offset = 0
  let pageLength = SORM_PAGE_SIZE
  while (pageLength === SORM_PAGE_SIZE) {
    const page = await querySormPage(
      { ...request, limit: SORM_PAGE_SIZE, offset },
      queryName,
      signal,
    )
    rows.push(...page.rows)
    pageLength = page.rows.length
    offset += SORM_PAGE_SIZE
  }
  return rows
}

function queryAccountPage(
  fields: FieldSpec[],
  params: AccountPageParams,
  queryName: string,
  signal: AbortSignal | undefined,
): Promise<SormPage> {
  const request = createQueryRequest({
    root_entity: 'Account',
    fields,
    sort: translateAccountSort(params.sort),
    ...(params.filters && { filters: translateAccountFilters(params.filters) }),
    ...(params.limit != null && { limit: params.limit }),
    ...(params.offset != null && { offset: params.offset }),
  })
  return querySormPage(request, queryName, signal)
}

async function querySleevesByAccount(
  accountRows: SormRow[],
  signal: AbortSignal | undefined,
): Promise<Map<string, AccountQuerySleeve[]>> {
  if (accountRows.length === 0) {
    return new Map()
  }
  const accountIds = accountRows.map((row) =>
    readSormString(row.moment_account_id),
  )
  const sleeveRows = await querySormAll(
    createQueryRequest({
      root_entity: 'Sleeve',
      fields: SLEEVE_SORM_FIELDS,
      filters: { 'Sleeve.account_id': { in: accountIds } },
    }),
    ACCOUNT_QUERY_SORM_QUERY_NAMES.accountSleeves,
    signal,
  )
  return groupSleevesByAccount(sleeveRows)
}

function toAccountListAccount(
  row: SormRow,
  sleeves: AccountQuerySleeve[] | undefined,
  includeMarginFields: boolean,
): AccountListAccount {
  const account: AccountListAccount = {
    moment_account_id: readSormString(row.moment_account_id),
    moment_organization_id: readSormString(row.moment_organization_id),
    custodian_id: readSormNullableString(row.custodian_id),
    custodian_account_id: readSormNullableString(row.custodian_account_id),
    moment_status: mapMomentStatus(row),
    custodian_status: readSormNullableString(row.custodian_status),
    moment_trading_group_id: readSormNullableString(row.risk_group_id),
    account_type: readSormNullableString(row.account_type),
    tax_state: readSormNullableString(row.tax_state),
    account_name: readSormNullableString(row.account_name),
    extra: mapAccountExtra(row),
    compliance_status: null,
    do_not_trade: readSormNullableBoolean(row.do_not_trade),
    do_not_trade_reason: readSormNullableString(row.do_not_trade_reason),
    statistics: mapAccountStatistics(row),
    advisor: {
      id: readSormNullableString(row.advisor_id),
      name: readSormNullableString(row.advisor_name),
    },
    custodian: mapCustodian(row),
    management_type: null,
    prime_brokerage_enabled: readSormNullableBoolean(
      row.prime_brokerage_enabled,
    ),
    compliance: null,
    sleeves:
      sleeves?.map((sleeve) => ({
        moment_sleeve_id: sleeve.moment_sleeve_id,
        target_allocation: sleeve.target_allocation,
        name: sleeve.name,
        model_assignment: sleeve.model_assignment,
      })) ?? null,
    proposals: null,
  }
  if (includeMarginFields) {
    account.do_not_buy = readSormNullableBoolean(row.do_not_buy)
    account.do_not_sell = readSormNullableBoolean(row.do_not_sell)
  }
  return account
}

interface AbbreviatedAccountOptions {
  sleeves: AccountQuerySleeve[] | undefined
  includeSleeves: boolean
  includeStatistics: boolean
  includeMarginFields: boolean
}

function toAbbreviatedAccount(
  row: SormRow,
  options: AbbreviatedAccountOptions,
): AbbreviatedAccount {
  const account: AbbreviatedAccount = {
    moment_account_id: readSormString(row.moment_account_id),
    custodian_account_id: readSormNullableString(row.custodian_account_id),
    account_name: readSormNullableString(row.account_name),
    account_type: readSormNullableString(row.account_type),
    household_id: readSormNullableString(row.household_id),
    management_type: null,
    moment_status: mapMomentStatus(row),
    moment_trading_group_id: readSormNullableString(row.risk_group_id),
    do_not_trade: readSormNullableBoolean(row.do_not_trade),
    do_not_trade_reason: readSormNullableString(row.do_not_trade_reason),
    cash_allocation: null,
    cash: mapCashLots(row),
    custodian: mapCustodian(row),
    prime_brokerage_enabled: readSormNullableBoolean(
      row.prime_brokerage_enabled,
    ),
    extra: mapAccountExtra(row),
  }
  if (options.includeMarginFields) {
    account.margin_indicator = readSormNullableBoolean(row.margin_indicator)
    account.do_not_buy = readSormNullableBoolean(row.do_not_buy)
    account.do_not_sell = readSormNullableBoolean(row.do_not_sell)
  }
  if (options.includeSleeves) {
    account.sleeves =
      options.sleeves?.map((sleeve) => ({
        is_default: sleeve.is_default,
        moment_sleeve_id: sleeve.moment_sleeve_id,
        name: sleeve.name,
      })) ?? null
  }
  if (options.includeStatistics) {
    account.statistics = {
      cash_market_value: readSormNumber(row.cash_market_value),
      total_market_value: readSormNumber(row.total_market_value),
    }
  }
  return account
}

/** SORM replacement for the legacy `AccountsList` GraphQL query. */
export async function fetchAccountListViaSorm(
  params: AccountListParams,
  signal?: AbortSignal,
): Promise<GetAccountListResponse> {
  const includeMarginFields = Boolean(params.include_margin_fields)
  const page = await queryAccountPage(
    includeMarginFields
      ? ACCOUNT_LIST_WITH_MARGIN_SORM_FIELDS
      : ACCOUNT_LIST_SORM_FIELDS,
    {
      ...params,
      limit: params.limit || DEFAULT_ACCOUNT_LIST_LIMIT,
      offset: params.offset || 0,
    },
    ACCOUNT_QUERY_SORM_QUERY_NAMES.accountList,
    signal,
  )
  const sleevesByAccount = await querySleevesByAccount(page.rows, signal)
  return {
    data: {
      accounts: {
        accounts: page.rows.map((row) =>
          toAccountListAccount(
            row,
            sleevesByAccount.get(readSormString(row.moment_account_id)),
            includeMarginFields,
          ),
        ),
        total_count: page.totalCount,
        total_fi_market_value: null,
      },
    },
  }
}

/** SORM replacement for the legacy `AccountIds` GraphQL query. */
export async function fetchAccountIdsViaSorm(
  params: AccountListParams,
  signal?: AbortSignal,
): Promise<AccountIdsResponse> {
  const page = await queryAccountPage(
    ACCOUNT_IDS_SORM_FIELDS,
    {
      ...params,
      limit: params.limit || DEFAULT_ACCOUNT_LIST_LIMIT,
      offset: params.offset || 0,
    },
    ACCOUNT_QUERY_SORM_QUERY_NAMES.accountIds,
    signal,
  )
  const sleevesByAccount = await querySleevesByAccount(page.rows, signal)
  return {
    data: {
      accounts: {
        accounts: page.rows.map((row) => {
          const accountId = readSormString(row.moment_account_id)
          const accountName = readSormNullableString(row.account_name)
          return {
            moment_account_id: accountId,
            ...(accountName != null && { account_name: accountName }),
            sleeves: (sleevesByAccount.get(accountId) ?? []).map((sleeve) => ({
              moment_sleeve_id: sleeve.moment_sleeve_id,
              is_default: sleeve.is_default,
              ...(sleeve.model_assignment && {
                model_assignment: {
                  model: { id: sleeve.model_assignment.model.id },
                },
              }),
            })),
          }
        }),
        total_count: page.totalCount,
      },
    },
  }
}

/** SORM replacement for the legacy `AbbreviatedAccountsList` GraphQL query. */
export async function fetchAbbreviatedAccountListViaSorm(
  params: AbbreviatedAccountsListQueryVariables & {
    include_margin_fields?: boolean
  },
  signal?: AbortSignal,
): Promise<GetAbbreviatedAccountListResponse> {
  const includeSleeves = Boolean(params.include_sleeves)
  const includeStatistics = Boolean(params.include_statistics)
  const includeMarginFields = Boolean(params.include_margin_fields)
  const page = await queryAccountPage(
    [
      ...ABBREVIATED_ACCOUNT_SORM_FIELDS,
      ...(includeStatistics ? ABBREVIATED_ACCOUNT_STATISTICS_SORM_FIELDS : []),
      ...(includeMarginFields ? ABBREVIATED_ACCOUNT_MARGIN_SORM_FIELDS : []),
    ],
    params,
    ACCOUNT_QUERY_SORM_QUERY_NAMES.abbreviatedAccountList,
    signal,
  )
  const sleevesByAccount = includeSleeves
    ? await querySleevesByAccount(page.rows, signal)
    : new Map<string, AccountQuerySleeve[]>()
  return {
    data: {
      accounts: {
        accounts: page.rows.map((row) =>
          toAbbreviatedAccount(row, {
            sleeves: sleevesByAccount.get(
              readSormString(row.moment_account_id),
            ),
            includeSleeves,
            includeStatistics,
            includeMarginFields,
          }),
        ),
        total_count: page.totalCount,
      },
    },
  }
}

/** SORM replacement for the legacy `AccountDntStatus` GraphQL query. */
export async function fetchAccountStatusesViaSorm(
  params: AccountStatusesParams,
  signal?: AbortSignal,
): Promise<AccountStatus[]> {
  if (params.accountIds.length === 0) {
    return []
  }
  const page = await querySormPage(
    createQueryRequest({
      root_entity: 'Account',
      fields: params.includeMarginFields
        ? ACCOUNT_STATUS_WITH_MARGIN_SORM_FIELDS
        : ACCOUNT_STATUS_SORM_FIELDS,
      filters: { 'Account.id': { in: params.accountIds } },
      limit: params.accountIds.length,
    }),
    ACCOUNT_QUERY_SORM_QUERY_NAMES.accountStatuses,
    signal,
  )
  return page.rows.map((row) => {
    const status: AccountStatus = {
      moment_account_id: readSormString(row.moment_account_id),
    }
    const doNotTrade = readSormNullableBoolean(row.do_not_trade)
    const doNotTradeReason = readSormNullableString(row.do_not_trade_reason)
    if (doNotTrade != null) status.do_not_trade = doNotTrade
    if (doNotTradeReason != null) status.do_not_trade_reason = doNotTradeReason
    if (params.includeMarginFields) {
      const doNotBuy = readSormNullableBoolean(row.do_not_buy)
      const doNotSell = readSormNullableBoolean(row.do_not_sell)
      if (doNotBuy != null) status.do_not_buy = doNotBuy
      if (doNotSell != null) status.do_not_sell = doNotSell
    }
    return status
  })
}

/** SORM replacement for the legacy `PositionsForInstrument` GraphQL query. */
export async function fetchPositionsForInstrumentViaSorm(
  params: PositionsForInstrumentQueryVariables,
  signal?: AbortSignal,
): Promise<{ data: PositionsForInstrumentQuery }> {
  const rows = await querySormAll(
    createQueryRequest({
      root_entity: 'Position',
      fields: POSITIONS_FOR_INSTRUMENT_SORM_FIELDS,
      filters: { 'Position.instrument_id': { equal_to: params.instrument_id } },
    }),
    ACCOUNT_QUERY_SORM_QUERY_NAMES.positionsForInstrument,
    signal,
  )
  return {
    data: {
      positions_for_instrument: { advisors: groupPositionsByAdvisor(rows) },
    },
  }
}
